import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FaTimes } from "react-icons/fa";
import { FiMapPin } from "react-icons/fi";
import { AdminNameCell, AdminRowActions, rowClickHandler } from "./AdminRowCells";

import axiosOrganizationInstance from "../organization/AxiosOrganizationInstance";
import { getAuthHeaders } from "../organization/organizationStorage";
import axiosRestaurantInstance from "../restaurant/AxiosRestaurantInstance";
import axiosGroceryInstance from "../restaurant/AxiosGroceryInstance";
import { GROCERY_API_PATHS, ORGANIZATION_API_PATHS, RESTAURANT_API_PATHS } from "../constants/apiPaths";
import { confirmAction } from "../restaurant/ui/feedback";
import { extractRestaurantId, extractRestaurantName } from "../restaurant/restaurantApi";
import { deleteOrganization, extractOrganizationId } from "../organization/organizationApi";
import { deleteStore, extractStoreId, extractStoreName, listStores } from "../store/storeApi";
import EatsAdminLayout from "./EatsAdminLayout";
import PickupHubsPage from "./PickupHubsPage";
import JuniorEatsSubscriptionsPage from "./JuniorEatsSubscriptionsPage";
import JuniorEatsMainMenuPage from "./JuniorEatsMainMenuPage";
import GlobalMenuMasterPage from "./GlobalMenuMasterPage";
import {
  formatFssaiDisplayOrFallback,
  formatMobileDisplayOrFallback,
  normalizeAdminEntityStatus,
} from "../restaurant/formatDisplay";
import CollapsibleFilters from "../restaurant/CollapsibleFilters";
import { useAutoDismissMessage } from "../utils/useAutoDismissMessage";
import "./EatsAdminCreatePage.css";
import "./EatsAdminDashboard.css";

const VALID_TABS = ["home", "restaurants", "organizations", "stores", "customers", "subscriptions", "jreats-menu", "menu-master", "pickup-hubs"];
const TAB_TITLE = {
  home: "Dashboard",
  restaurants: "Restaurants",
  organizations: "Organizations",
  stores: "Stores",
  customers: "Customers",
  subscriptions: "Subscriptions",
  "jreats-menu": "Jr Eats Menu",
  "menu-master": "Menu Master",
  "pickup-hubs": "Pickup Hubs",
};

const getOrganizationDisplayName = (org) =>
  org?.name || org?.organizationName || org?.Name || "-";

const getOrganizationDisplayCity = (org) => {
  if (org?.city) {
    return org.city;
  }
  const addresses = org?.addresses;
  if (Array.isArray(addresses) && addresses.length > 0) {
    return addresses[0]?.city || "-";
  }
  return org?.postalCode || "-";
};

const getOrganizationStatus = (org) => (org?.status ? normalizeAdminEntityStatus(org.status) : null);

const getRestaurantDisplayCity = (restaurant) => {
  if (restaurant?.city) {
    return restaurant.city;
  }
  const addresses = restaurant?.addresses;
  if (Array.isArray(addresses) && addresses.length > 0) {
    return addresses[0]?.city || "-";
  }
  return "-";
};

const pickField = (row, ...keys) => {
  for (const key of keys) {
    const value = row?.[key];
    if (value != null && String(value).trim() !== "") return value;
  }
  return null;
};

const INDIAN_STATE_CODES = {
  "andaman and nicobar islands": "AN",
  "andhra pradesh": "AP",
  "arunachal pradesh": "AR",
  assam: "AS",
  bihar: "BR",
  chandigarh: "CH",
  chhattisgarh: "CG",
  "dadra and nagar haveli and daman and diu": "DD",
  delhi: "DL",
  "nct of delhi": "DL",
  goa: "GA",
  gujarat: "GJ",
  haryana: "HR",
  "himachal pradesh": "HP",
  "jammu and kashmir": "JK",
  jharkhand: "JH",
  karnataka: "KA",
  kerala: "KL",
  ladakh: "LA",
  lakshadweep: "LD",
  "madhya pradesh": "MP",
  maharashtra: "MH",
  manipur: "MN",
  meghalaya: "ML",
  mizoram: "MZ",
  nagaland: "NL",
  odisha: "OD",
  puducherry: "PY",
  punjab: "PB",
  rajasthan: "RJ",
  sikkim: "SK",
  "tamil nadu": "TN",
  telangana: "TG",
  tripura: "TR",
  "uttar pradesh": "UP",
  uttarakhand: "UK",
  "west bengal": "WB",
};

const stateCode = (state) => {
  const key = String(state || "").trim().toLowerCase().replace(/&/g, "and").replace(/\s+/g, " ");
  return INDIAN_STATE_CODES[key] || state;
};

const getRestaurantLocation = (restaurant) => {
  const address = Array.isArray(restaurant?.addresses) ? restaurant.addresses[0] : null;
  const city = pickField(restaurant, "city") || pickField(address, "city");
  const state = pickField(restaurant, "state") || pickField(address, "state");
  return [city, state ? stateCode(state) : null].filter(Boolean).join(", ") || "-";
};

const getGoogleMapsUrl = (entity, name) => {
  const address = Array.isArray(entity?.addresses) ? entity.addresses[0] : null;
  const lat = pickField(entity, "lat", "latitude") || pickField(address, "lat", "latitude");
  const lon =
    pickField(entity, "lon", "lng", "longitude") || pickField(address, "lon", "lng", "longitude");
  let query;
  if (lat && lon && Number(lat) && Number(lon)) {
    query = `${lat},${lon}`;
  } else {
    query = [
      name && name !== "-" ? name : null,
      pickField(entity, "addressLine1", "address_line1") || pickField(address, "addressLine1", "address_line1"),
      pickField(entity, "areaName", "area_name") || pickField(address, "areaName", "area_name"),
      pickField(entity, "city") || pickField(address, "city"),
      pickField(entity, "state") || pickField(address, "state"),
    ]
      .filter(Boolean)
      .join(", ");
  }
  return query ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}` : null;
};

const LocationCell = ({ location, mapUrl }) => (
  <span className="eats-admin-location-cell">
    <span>{location}</span>
    {mapUrl && location !== "-" ? (
      <a
        href={mapUrl}
        target="_blank"
        rel="noreferrer"
        className="eats-admin-map-link"
        title="Open in Google Maps"
        aria-label={`Open ${location} in Google Maps`}
      >
        <FiMapPin aria-hidden="true" />
      </a>
    ) : null}
  </span>
);

const getStoreDisplayCity = (store) => store?.city || "-";

const formatDashboardCount = (value, loading) => {
  if (loading) return "…";
  if (value == null) return "—";
  return value;
};

const renderStatusBadge = (status) => {
  if (!status) return "-";
  const normalized = String(status).toUpperCase();
  const className =
    normalized === "ACTIVE"
      ? "eats-admin-status eats-admin-status--active"
      : normalized === "INACTIVE"
        ? "eats-admin-status eats-admin-status--inactive"
        : "eats-admin-status eats-admin-status--default";
  return <span className={className} title={status}>{status}</span>;
};

const ADMIN_ROWS_PER_PAGE = 10;

const AdminTablePagination = ({ page, totalItems, onPageChange }) => {
  const totalPages = Math.max(1, Math.ceil(totalItems / ADMIN_ROWS_PER_PAGE));
  if (totalItems <= ADMIN_ROWS_PER_PAGE) return null;
  const start = (page - 1) * ADMIN_ROWS_PER_PAGE + 1;
  const end = Math.min(page * ADMIN_ROWS_PER_PAGE, totalItems);
  return (
    <div className="eats-admin-pagination">
      <span className="eats-admin-pagination__info">
        {start}-{end} of {totalItems}
      </span>
      <div className="eats-admin-pagination__controls">
        <button
          type="button"
          className="eats-admin-btn eats-admin-btn--ghost"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
        >
          Prev
        </button>
        <span className="eats-admin-pagination__page">
          Page {page} of {totalPages}
        </span>
        <button
          type="button"
          className="eats-admin-btn eats-admin-btn--ghost"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
};

const AdminClearableInput = ({
  value,
  onChange,
  onClear,
  placeholder,
  suggestions,
  getSuggestionKey,
  renderSuggestion,
  onSelectSuggestion,
  onEnter,
}) => {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const list = Array.isArray(suggestions) ? suggestions : [];
  const showList = open && list.length > 0;

  useEffect(() => {
    setHighlight(-1);
  }, [suggestions]);

  const select = (item) => {
    setOpen(false);
    setHighlight(-1);
    onSelectSuggestion?.(item);
  };

  const handleKeyDown = (e) => {
    if (showList && e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => (h + 1) % list.length);
    } else if (showList && e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => (h <= 0 ? list.length - 1 : h - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (showList && highlight >= 0) {
        select(list[highlight]);
      } else {
        setOpen(false);
        onEnter?.();
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
  <div className="eats-admin-input-wrap">
    <input
      className="eats-admin-input"
      placeholder={placeholder}
      value={value}
      onChange={(e) => {
        setOpen(true);
        onChange(e);
      }}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      onKeyDown={handleKeyDown}
      autoComplete="off"
      role={suggestions ? "combobox" : undefined}
      aria-expanded={suggestions ? showList : undefined}
      aria-autocomplete={suggestions ? "list" : undefined}
    />
    {showList ? (
      <ul className="eats-admin-suggestions" role="listbox">
        {list.map((item, i) => (
          <li
            key={getSuggestionKey ? getSuggestionKey(item, i) : i}
            role="option"
            aria-selected={i === highlight}
            className={`eats-admin-suggestions__item${i === highlight ? " is-active" : ""}`}
            onMouseDown={(e) => {
              e.preventDefault();
              select(item);
            }}
            onMouseEnter={() => setHighlight(i)}
          >
            {renderSuggestion ? renderSuggestion(item) : String(item)}
          </li>
        ))}
      </ul>
    ) : null}
    {value ? (
      <button
        type="button"
        className="eats-admin-input-clear"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onClear?.();
        }}
        aria-label="Clear search"
      >
        <FaTimes aria-hidden />
      </button>
    ) : null}
  </div>
  );
};

const RESTAURANT_SEARCH_STORAGE_KEY = "eatsAdminRestaurantSearch";
const STORE_SEARCH_STORAGE_KEY = "eatsAdminStoreSearch";
const ORG_SEARCH_STORAGE_KEY = "eatsAdminOrganizationSearch";

function readSavedSearch(key) {
  try {
    return JSON.parse(sessionStorage.getItem(key) || "null");
  } catch {
    return null;
  }
}

const EatsAdminDashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [savedRestaurantSearch] = useState(() => {
    const saved = readSavedSearch(RESTAURANT_SEARCH_STORAGE_KEY);
    const wantsPending = searchParams.get("tab") === "restaurants" && searchParams.get("view") === "pending";
    if (!saved || (wantsPending && saved.statusFilter !== "PENDING")) return null;
    return saved;
  });
  const [activeTab, setActiveTab] = useState("home");
  const navigate = useNavigate();

  const [savedOrgSearch] = useState(() => {
    const saved = readSavedSearch(ORG_SEARCH_STORAGE_KEY);
    const wantsPending = searchParams.get("tab") === "organizations" && searchParams.get("view") === "pending";
    if (!saved || (wantsPending && saved.statusFilter !== "PENDING")) return null;
    return saved;
  });
  const [orgSearch, setOrgSearch] = useState(() => savedOrgSearch?.search || "");
  const [orgStatusFilter, setOrgStatusFilter] = useState(
    () =>
      savedOrgSearch?.statusFilter ||
      (searchParams.get("tab") === "organizations" && searchParams.get("view") === "pending" ? "PENDING" : "ACTIVE")
  );
  const [orgResults, setOrgResults] = useState(() =>
    Array.isArray(savedOrgSearch?.results) ? savedOrgSearch.results : []
  );
  const [orgError, setOrgError] = useState("");
  const [orgLoading, setOrgLoading] = useState(false);
  const [orgPage, setOrgPage] = useState(() => savedOrgSearch?.page || 1);
  const [orgSuggestions, setOrgSuggestions] = useState([]);

  useEffect(() => {
    try {
      sessionStorage.setItem(
        ORG_SEARCH_STORAGE_KEY,
        JSON.stringify({
          search: orgSearch,
          statusFilter: orgStatusFilter,
          results: orgResults,
          page: orgPage,
        })
      );
    } catch {
      /* storage full or unavailable */
    }
  }, [orgSearch, orgStatusFilter, orgResults, orgPage]);

  const [restaurantSearch, setRestaurantSearch] = useState(() => savedRestaurantSearch?.search || "");
  const [restaurantStatusFilter, setRestaurantStatusFilter] = useState(
    () =>
      savedRestaurantSearch?.statusFilter ||
      (searchParams.get("tab") === "restaurants" && searchParams.get("view") === "pending" ? "PENDING" : "ACTIVE")
  );
  const [restaurantResults, setRestaurantResults] = useState(() =>
    Array.isArray(savedRestaurantSearch?.results) ? savedRestaurantSearch.results : []
  );
  const [restaurantError, setRestaurantError] = useState("");
  const [restaurantLoading, setRestaurantLoading] = useState(false);
  const [restaurantPage, setRestaurantPage] = useState(() => savedRestaurantSearch?.page || 1);

  useEffect(() => {
    try {
      sessionStorage.setItem(
        RESTAURANT_SEARCH_STORAGE_KEY,
        JSON.stringify({
          search: restaurantSearch,
          statusFilter: restaurantStatusFilter,
          results: restaurantResults,
          page: restaurantPage,
        })
      );
    } catch {
      /* storage full or unavailable */
    }
  }, [restaurantSearch, restaurantStatusFilter, restaurantResults, restaurantPage]);
  const [restaurantSuggestions, setRestaurantSuggestions] = useState([]);
  const [pendingCompanies, setPendingCompanies] = useState([]);
  const [pendingOrganizations, setPendingOrganizations] = useState([]);
  const [pendingStores, setPendingStores] = useState([]);
  const [dashboardStats, setDashboardStats] = useState({
    organizations: null,
    restaurants: null,
    stores: null,
    customers: null,
  });
  const [dashboardStatsLoading, setDashboardStatsLoading] = useState(true);

  const [savedStoreSearch] = useState(() => {
    const saved = readSavedSearch(STORE_SEARCH_STORAGE_KEY);
    const wantsPending = searchParams.get("tab") === "stores" && searchParams.get("view") === "pending";
    if (!saved || (wantsPending && saved.statusFilter !== "PENDING")) return null;
    return saved;
  });
  const [storeSearch, setStoreSearch] = useState(() => savedStoreSearch?.search || "");
  const [storeStatusFilter, setStoreStatusFilter] = useState(
    () =>
      savedStoreSearch?.statusFilter ||
      (searchParams.get("tab") === "stores" && searchParams.get("view") === "pending" ? "PENDING" : "ACTIVE")
  );
  const [storeResults, setStoreResults] = useState(() =>
    Array.isArray(savedStoreSearch?.results) ? savedStoreSearch.results : []
  );
  const [storeError, setStoreError] = useState("");
  const [storeLoading, setStoreLoading] = useState(false);
  const [storePage, setStorePage] = useState(() => savedStoreSearch?.page || 1);
  const [storeSuggestions, setStoreSuggestions] = useState([]);

  useEffect(() => {
    try {
      sessionStorage.setItem(
        STORE_SEARCH_STORAGE_KEY,
        JSON.stringify({
          search: storeSearch,
          statusFilter: storeStatusFilter,
          results: storeResults,
          page: storePage,
        })
      );
    } catch {
      /* storage full or unavailable */
    }
  }, [storeSearch, storeStatusFilter, storeResults, storePage]);

  const [customerCriteria, setCustomerCriteria] = useState({
    name: "",
    contactNo: "",
    createdSource: "",
  });
  const [customerResults, setCustomerResults] = useState([]);
  const [customerError, setCustomerError] = useState("");
  const [customerLoading, setCustomerLoading] = useState(false);
  const [customerPage, setCustomerPage] = useState(1);
  useAutoDismissMessage(orgError, setOrgError);
  useAutoDismissMessage(restaurantError, setRestaurantError);
  useAutoDismissMessage(storeError, setStoreError);
  useAutoDismissMessage(customerError, setCustomerError);

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab === "pickup-pricing") {
      setActiveTab("pickup-hubs");
      setSearchParams({ tab: "pickup-hubs" }, { replace: true });
      return;
    }
    if (tab && VALID_TABS.includes(tab)) {
      setActiveTab(tab);
      return;
    }
    setActiveTab("home");
    setSearchParams({ tab: "home" }, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const loadPendingCompanies = async () => {
      try {
        const res = await axiosRestaurantInstance.get("/restaurant/pending-companies", {
          headers: getAuthHeaders(),
        });
        const list = Array.isArray(res?.data?.companies) ? res.data.companies.filter((c) => c && c.restaurantId) : [];
        setPendingCompanies(list);
      } catch (error) {
        console.error("Failed loading pending companies from backend:", error?.response?.data || error?.message);
        setPendingCompanies([]);
      }
    };
    const loadPendingOrganizations = async () => {
      try {
        const res = await axiosOrganizationInstance.get("/organization/pending-organizations", {
          headers: getAuthHeaders(),
        });
        const list = Array.isArray(res?.data?.organizations)
          ? res.data.organizations.filter((o) => o && o.organizationId)
          : [];
        setPendingOrganizations(list);
      } catch (error) {
        console.error(
          "Failed loading pending organizations from backend:",
          error?.response?.data || error?.message
        );
        setPendingOrganizations([]);
      }
    };

    const loadPendingStores = async () => {
      try {
        const res = await axiosGroceryInstance.get(GROCERY_API_PATHS.pendingStores, {
          headers: getAuthHeaders(),
        });
        const list = Array.isArray(res?.data?.stores)
          ? res.data.stores.filter((s) => s && s.storeId)
          : [];
        setPendingStores(list);
      } catch (error) {
        console.error("Failed loading pending stores from backend:", error?.response?.data || error?.message);
        setPendingStores([]);
      }
    };

    const loadDashboardStats = async () => {
      setDashboardStatsLoading(true);
      try {
        const res = await axiosOrganizationInstance.get("/admin/dashboard-stats", {
          headers: getAuthHeaders(),
        });
        const data = res?.data || {};
        setDashboardStats({
          organizations: Number(data.organizations) || 0,
          restaurants: Number(data.restaurants) || 0,
          stores: Number(data.stores) || 0,
          customers: Number(data.customers) || 0,
        });
      } catch (error) {
        console.error("Failed loading dashboard stats:", error?.response?.data || error?.message);
        setDashboardStats({
          organizations: null,
          restaurants: null,
          stores: null,
          customers: null,
        });
      } finally {
        setDashboardStatsLoading(false);
      }
    };

    loadPendingCompanies();
    loadPendingOrganizations();
    loadPendingStores();
    loadDashboardStats();
    const onFocus = () => {
      loadPendingCompanies();
      loadPendingOrganizations();
      loadPendingStores();
      loadDashboardStats();
    };
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  useEffect(() => {
    if (restaurantStatusFilter !== "PENDING") return;
    if (restaurantSearch.trim()) return;
    setRestaurantResults(pendingCompanies);
  }, [pendingCompanies, restaurantStatusFilter, restaurantSearch]);

  useEffect(() => {
    if (storeStatusFilter !== "PENDING") return;
    if (storeSearch.trim()) return;
    setStoreResults(pendingStores);
  }, [pendingStores, storeStatusFilter, storeSearch]);

  useEffect(() => {
    if (orgStatusFilter !== "PENDING") return;
    if (orgSearch.trim()) return;
    setOrgResults(pendingOrganizations);
  }, [pendingOrganizations, orgStatusFilter, orgSearch]);

  const continuePendingRestaurantCreate = (restaurantId) => {
    navigate(`/eats/admin/restaurants/create?restaurantId=${restaurantId}`);
  };

  const continuePendingOrganizationCreate = (organizationId) => {
    navigate(`/eats/admin/organizations/create?organizationId=${organizationId}`);
  };

  const continuePendingStoreCreate = (storeId) => {
    navigate(`/eats/admin/stores/create?storeId=${storeId}`);
  };

  const handleTabClick = (tab) => {
    if (!VALID_TABS.includes(tab)) return;
    setActiveTab(tab);
    setSearchParams({ tab }, { replace: true });
  };

  const fetchOrganizationMatches = async (text, statusFilter) => {
    const query = text.toLowerCase();
    const matches = (o) => {
      if (!query) return true;
      const name = String(getOrganizationDisplayName(o) || "").toLowerCase();
      const code = String(o.organizationCode || "").toLowerCase();
      const id = String(extractOrganizationId(o) || "").toLowerCase();
      return name.includes(query) || code.includes(query) || id.includes(query);
    };
    if (statusFilter === "PENDING") return pendingOrganizations.filter(matches);

    const res = await axiosOrganizationInstance.get(ORGANIZATION_API_PATHS.adminOrganizations, {
      headers: getAuthHeaders(),
    });
    const rows = Array.isArray(res?.data?.organizations) ? res.data.organizations : [];
    return rows.filter(
      (o) => matches(o) && (statusFilter === "ALL" || getOrganizationStatus(o) === statusFilter)
    );
  };

  useEffect(() => {
    const text = orgSearch.trim();
    if (activeTab !== "organizations" || !text) {
      setOrgSuggestions([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const rows = await fetchOrganizationMatches(text, orgStatusFilter);
        if (!cancelled) setOrgSuggestions(rows.slice(0, 8));
      } catch {
        if (!cancelled) setOrgSuggestions([]);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgSearch, orgStatusFilter, activeTab, pendingOrganizations]);

  const selectOrganizationSuggestion = (org) => {
    setOrgSearch(getOrganizationDisplayName(org) === "-" ? "" : getOrganizationDisplayName(org));
    setOrgSuggestions([]);
    setOrgError("");
    setOrgPage(1);
    setOrgResults([org]);
  };

  useEffect(() => {
    if (!savedOrgSearch || savedOrgSearch.statusFilter === "PENDING") return undefined;
    if (!Array.isArray(savedOrgSearch.results) || savedOrgSearch.results.length === 0) return undefined;
    let cancelled = false;
    fetchOrganizationMatches(String(savedOrgSearch.search || "").trim(), savedOrgSearch.statusFilter)
      .then((rows) => {
        if (!cancelled) setOrgResults(rows);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deactivateOrganization = async (organizationId, name) => {
    const ok = await confirmAction({
      title: "Deactivate organization?",
      message: `Deactivate ${name || "this organization"}? It will no longer be available to customers.`,
      confirmLabel: "Deactivate",
      danger: true,
    });
    if (!ok) return;
    try {
      await deleteOrganization(organizationId);
      setOrgResults((rows) => {
        const updated = rows.map((o) =>
          String(extractOrganizationId(o)) === String(organizationId) ? { ...o, status: "INACTIVE" } : o
        );
        return orgStatusFilter === "ACTIVE"
          ? updated.filter((o) => getOrganizationStatus(o) === "ACTIVE")
          : updated;
      });
    } catch (e) {
      setOrgError(e?.response?.data?.message || "Failed to deactivate organization");
    }
  };

  const searchOrganizations = async () => {
    setOrgLoading(true);
    setOrgError("");
    setOrgPage(1);
    try {
      const rows = await fetchOrganizationMatches(orgSearch.trim(), orgStatusFilter);
      setOrgResults(rows);
    } catch (e) {
      if (e?.response?.status === 401) {
        setOrgError("Session expired. Please sign in again.");
      } else {
        setOrgError(
          e?.response?.data?.message ||
            e?.response?.data?.error ||
            "Failed to search organizations"
        );
      }
      setOrgResults([]);
    } finally {
      setOrgLoading(false);
    }
  };

  const fetchRestaurantMatches = async (text, statusFilter) => {
    if (statusFilter === "PENDING") {
      const query = text.toLowerCase();
      return pendingCompanies.filter((c) => {
        if (!query) return true;
        const name = String(c.restaurantName || "").toLowerCase();
        const code = String(c.restaurantShortCode || "").toLowerCase();
        const id = String(c.restaurantId || "").toLowerCase();
        return name.includes(query) || code.includes(query) || id.includes(query);
      });
    }

    const res = await axiosRestaurantInstance.get("/restaurant/search", {
      params: { searchText: text || undefined },
    });
    const rows = res?.data?.restaurants || res?.data || [];
    const list = Array.isArray(rows) ? rows : [];
    return statusFilter === "ALL"
      ? list
      : list.filter((r) => String(r.status || "").toUpperCase() === statusFilter);
  };

  useEffect(() => {
    const text = restaurantSearch.trim();
    if (activeTab !== "restaurants" || !text) {
      setRestaurantSuggestions([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const rows = await fetchRestaurantMatches(text, restaurantStatusFilter);
        if (!cancelled) setRestaurantSuggestions(rows.slice(0, 8));
      } catch {
        if (!cancelled) setRestaurantSuggestions([]);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantSearch, restaurantStatusFilter, activeTab, pendingCompanies]);

  const selectRestaurantSuggestion = (restaurant) => {
    setRestaurantSearch(extractRestaurantName(restaurant) || restaurant.restaurantName || "");
    setRestaurantSuggestions([]);
    setRestaurantError("");
    setRestaurantPage(1);
    setRestaurantResults([restaurant]);
  };

  useEffect(() => {
    const text = savedRestaurantSearch?.search?.trim();
    if (!text || savedRestaurantSearch.statusFilter === "PENDING") return undefined;
    let cancelled = false;
    fetchRestaurantMatches(text, savedRestaurantSearch.statusFilter)
      .then((rows) => {
        if (!cancelled) setRestaurantResults(rows);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const searchRestaurants = async () => {
    setRestaurantLoading(true);
    setRestaurantError("");
    setRestaurantPage(1);
    try {
      const rows = await fetchRestaurantMatches(restaurantSearch.trim(), restaurantStatusFilter);
      setRestaurantResults(rows);
    } catch (e) {
      setRestaurantError(e?.response?.data?.message || e?.response?.data?.error || "Failed to search restaurants");
      setRestaurantResults([]);
    } finally {
      setRestaurantLoading(false);
    }
  };

  const deactivateRestaurant = async (restaurantId, name) => {
    const ok = await confirmAction({
      title: "Deactivate restaurant?",
      message: `Deactivate ${name || "this restaurant"}? It will no longer be available to customers.`,
      confirmLabel: "Deactivate",
      danger: true,
    });
    if (!ok) return;
    try {
      const token = localStorage.getItem("Token");
      await axiosRestaurantInstance.delete(RESTAURANT_API_PATHS.restaurantDeactivate(restaurantId), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setRestaurantResults((rows) => {
        const updated = rows.map((r) =>
          String(extractRestaurantId(r) || r.restaurantId) === String(restaurantId)
            ? { ...r, status: "INACTIVE" }
            : r
        );
        return restaurantStatusFilter === "ACTIVE"
          ? updated.filter((r) => String(r.status || "").toUpperCase() === "ACTIVE")
          : updated;
      });
    } catch (e) {
      setRestaurantError(e?.response?.data?.message || "Failed to deactivate restaurant");
    }
  };

  const fetchStoreMatches = async (text, statusFilter) => {
    const query = text.toLowerCase();
    const matches = (s) => {
      if (!query) return true;
      const name = String(extractStoreName(s) || "").toLowerCase();
      const code = String(s.shortCode || "").toLowerCase();
      const id = String(extractStoreId(s) || "").toLowerCase();
      return name.includes(query) || code.includes(query) || id.includes(query);
    };
    if (statusFilter === "PENDING") return pendingStores.filter(matches);

    const rows = await listStores();
    return rows.filter(
      (s) =>
        matches(s) &&
        (statusFilter === "ALL" || String(s.status || "").toUpperCase() === statusFilter)
    );
  };

  useEffect(() => {
    const text = storeSearch.trim();
    if (activeTab !== "stores" || !text) {
      setStoreSuggestions([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const rows = await fetchStoreMatches(text, storeStatusFilter);
        if (!cancelled) setStoreSuggestions(rows.slice(0, 8));
      } catch {
        if (!cancelled) setStoreSuggestions([]);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeSearch, storeStatusFilter, activeTab, pendingStores]);

  const selectStoreSuggestion = (store) => {
    setStoreSearch(extractStoreName(store) || "");
    setStoreSuggestions([]);
    setStoreError("");
    setStorePage(1);
    setStoreResults([store]);
  };

  useEffect(() => {
    if (!savedStoreSearch || savedStoreSearch.statusFilter === "PENDING") return undefined;
    if (!Array.isArray(savedStoreSearch.results) || savedStoreSearch.results.length === 0) return undefined;
    let cancelled = false;
    fetchStoreMatches(String(savedStoreSearch.search || "").trim(), savedStoreSearch.statusFilter)
      .then((rows) => {
        if (!cancelled) setStoreResults(rows);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deactivateStore = async (storeId, name) => {
    const ok = await confirmAction({
      title: "Deactivate store?",
      message: `Deactivate ${name || "this store"}? It will no longer be available to customers.`,
      confirmLabel: "Deactivate",
      danger: true,
    });
    if (!ok) return;
    try {
      await deleteStore(storeId);
      setStoreResults((rows) => {
        const updated = rows.map((s) =>
          String(extractStoreId(s)) === String(storeId) ? { ...s, status: "INACTIVE" } : s
        );
        return storeStatusFilter === "ACTIVE"
          ? updated.filter((s) => String(s.status || "").toUpperCase() === "ACTIVE")
          : updated;
      });
    } catch (e) {
      setStoreError(e?.response?.data?.message || "Failed to deactivate store");
    }
  };

  const searchStores = async () => {
    setStoreLoading(true);
    setStoreError("");
    setStorePage(1);
    try {
      const rows = await fetchStoreMatches(storeSearch.trim(), storeStatusFilter);
      setStoreResults(rows);
    } catch (e) {
      if (e?.response?.status === 401) {
        setStoreError("Session expired. Please sign in again.");
      } else {
        setStoreError(e?.response?.data?.message || e?.response?.data?.error || "Failed to search stores");
      }
      setStoreResults([]);
    } finally {
      setStoreLoading(false);
    }
  };

  const clearCustomerFilterField = (field) => {
    setCustomerCriteria((prev) => {
      const next = { ...prev, [field]: "" };
      const allEmpty = Object.values(next).every((v) => String(v || "").trim() === "");
      if (allEmpty) {
        setCustomerResults([]);
        setCustomerError("");
      }
      return next;
    });
  };

  const searchCustomers = async () => {
    const name = String(customerCriteria.name || "").trim();
    const contactNo = String(customerCriteria.contactNo || "").trim();
    const createdSource = String(customerCriteria.createdSource || "").trim();

    if (!name && !contactNo && !createdSource) {
      setCustomerError("Enter at least one search criterion.");
      setCustomerResults([]);
      return;
    }

    const params = {};
    if (name) params.name = name;
    if (contactNo) params.contactNo = contactNo;
    if (createdSource) params.createdSource = createdSource;

    setCustomerLoading(true);
    setCustomerError("");
    setCustomerPage(1);
    try {
      const res = await axiosOrganizationInstance.get(ORGANIZATION_API_PATHS.adminCustomerSearch, {
        params,
        headers: getAuthHeaders(),
      });
      const rows = res?.data?.customers || [];
      setCustomerResults(Array.isArray(rows) ? rows : []);
    } catch (e) {
      setCustomerError(e?.response?.data?.message || e?.response?.data?.error || "Failed to search customers");
      setCustomerResults([]);
    } finally {
      setCustomerLoading(false);
    }
  };

  const handleRestaurantStatusFilterChange = (value) => {
    setRestaurantStatusFilter(value);
    setRestaurantPage(1);
    setRestaurantError("");
    if (value === "PENDING") {
      setRestaurantResults(pendingCompanies);
      setSearchParams({ tab: "restaurants", view: "pending" }, { replace: true });
    } else {
      setRestaurantResults([]);
      setSearchParams({ tab: "restaurants" }, { replace: true });
    }
  };

  const handleOrgStatusFilterChange = (value) => {
    setOrgStatusFilter(value);
    setOrgPage(1);
    setOrgError("");
    if (value === "PENDING") {
      setOrgResults(pendingOrganizations);
      setSearchParams({ tab: "organizations", view: "pending" }, { replace: true });
    } else {
      setOrgResults([]);
      setSearchParams({ tab: "organizations" }, { replace: true });
    }
  };

  const handleStoreStatusFilterChange = (value) => {
    setStoreStatusFilter(value);
    setStorePage(1);
    setStoreError("");
    if (value === "PENDING") {
      setStoreResults(pendingStores);
      setSearchParams({ tab: "stores", view: "pending" }, { replace: true });
    } else {
      setStoreResults([]);
      setSearchParams({ tab: "stores" }, { replace: true });
    }
  };

  return (
    <EatsAdminLayout activeTab={activeTab}>
          <h2 className="eats-admin-dashboard__title">{TAB_TITLE[activeTab] || "Dashboard"}</h2>
          <p className="eats-admin-dashboard__subtitle">
            Search restaurants, organizations, stores, and customers. Manage creation flows from this admin area.
          </p>

          <div className="eats-admin-dashboard">
            {activeTab === "home" ? (
              <div className="eats-admin-panel eats-admin-panel--home">
                <div className="eats-admin-stat-grid">
                  <div className="eats-admin-stat-card eats-admin-stat-card--restaurants">
                    <div className="eats-admin-stat-card__label">Restaurants</div>
                    <div className="eats-admin-stat-card__value">
                      {formatDashboardCount(dashboardStats.restaurants, dashboardStatsLoading)}
                    </div>
                    <div className="eats-admin-stat-card__meta">
                      {pendingCompanies.length} pending setup
                    </div>
                  </div>
                  <div className="eats-admin-stat-card eats-admin-stat-card--organizations">
                    <div className="eats-admin-stat-card__label">Organizations</div>
                    <div className="eats-admin-stat-card__value">
                      {formatDashboardCount(dashboardStats.organizations, dashboardStatsLoading)}
                    </div>
                    <div className="eats-admin-stat-card__meta">
                      {pendingOrganizations.length} pending setup
                    </div>
                  </div>
                  <div className="eats-admin-stat-card eats-admin-stat-card--stores">
                    <div className="eats-admin-stat-card__label">Stores</div>
                    <div className="eats-admin-stat-card__value">
                      {formatDashboardCount(dashboardStats.stores, dashboardStatsLoading)}
                    </div>
                    <div className="eats-admin-stat-card__meta">
                      {pendingStores.length} pending setup
                    </div>
                  </div>
                  <div className="eats-admin-stat-card eats-admin-stat-card--customers">
                    <div className="eats-admin-stat-card__label">Customers</div>
                    <div className="eats-admin-stat-card__value">
                      {formatDashboardCount(dashboardStats.customers, dashboardStatsLoading)}
                    </div>
                    <div className="eats-admin-stat-card__meta">Registered customers</div>
                  </div>
                </div>
                <ul className="eats-admin-hint-list">
                  <li>Use the left sidebar to switch modules.</li>
                  <li>Default dashboard route opens this Home view.</li>
                  <li>Create restaurant, organization, and store open in admin internal pages.</li>
                </ul>
              </div>
            ) : null}

            {activeTab === "organizations" ? (
              <div className="eats-admin-panel">
                <div className="eats-admin-search-bar eats-admin-search-bar--organizations">
                  <AdminClearableInput
                    placeholder="Search by organization name, code, or ID"
                    value={orgSearch}
                    onChange={(e) => setOrgSearch(e.target.value)}
                    onClear={() => {
                      setOrgSearch("");
                      setOrgError("");
                      setOrgResults(orgStatusFilter === "PENDING" ? pendingOrganizations : []);
                    }}
                    suggestions={orgSuggestions}
                    getSuggestionKey={(o, i) => extractOrganizationId(o) || i}
                    renderSuggestion={(o) => {
                      const meta =
                        orgStatusFilter === "PENDING"
                          ? String(o.organizationCode || "").toUpperCase()
                          : getOrganizationDisplayCity(o);
                      return (
                        <>
                          <span className="eats-admin-suggestions__name">{getOrganizationDisplayName(o)}</span>
                          {meta && meta !== "-" ? <span className="eats-admin-suggestions__meta">{meta}</span> : null}
                        </>
                      );
                    }}
                    onSelectSuggestion={selectOrganizationSuggestion}
                    onEnter={searchOrganizations}
                  />
                  <CollapsibleFilters activeCount={orgStatusFilter !== "ACTIVE" ? 1 : 0}>
                    <select
                      className="eats-admin-input eats-admin-status-filter"
                      value={orgStatusFilter}
                      onChange={(e) => handleOrgStatusFilterChange(e.target.value)}
                      aria-label="Organization status"
                    >
                      <option value="ACTIVE">Active</option>
                      <option value="INACTIVE">Inactive</option>
                      <option value="PENDING">Pending</option>
                      <option value="ALL">All</option>
                    </select>
                  </CollapsibleFilters>
                  <button
                    type="button"
                    onClick={searchOrganizations}
                    disabled={orgLoading}
                    className="eats-admin-btn eats-admin-btn--primary"
                  >
                    {orgLoading ? "Searching..." : "Search"}
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate("/eats/admin/organizations/create")}
                    className="eats-admin-btn eats-admin-btn--create"
                  >
                    Create Organization
                  </button>
                </div>

                {orgError ? <div className="eats-admin-error">{orgError}</div> : null}
                <div className="eats-admin-table-wrap">
                  <table className="eats-admin-table eats-admin-table--restaurants eats-admin-table--entities">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Type</th>
                        <th>Location</th>
                        <th>Phone</th>
                        <th>Email</th>
                        <th className="eats-admin-col-code">Short Code</th>
                        <th className="eats-admin-col-status">Status</th>
                        <th className="eats-admin-col-actions">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orgResults.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="eats-admin-table__empty">
                            {orgStatusFilter === "PENDING" ? "No pending organizations." : "No organization results."}
                          </td>
                        </tr>
                      ) : orgResults
                        .slice((orgPage - 1) * ADMIN_ROWS_PER_PAGE, orgPage * ADMIN_ROWS_PER_PAGE)
                        .map((o, i) => {
                        const id = extractOrganizationId(o);
                        const name = getOrganizationDisplayName(o);
                        const status = getOrganizationStatus(o);
                        const isPending = orgStatusFilter === "PENDING";
                        const isActive = status === "ACTIVE";
                        const detailPath = id ? `/eats/admin/organizations/${id}` : null;
                        const phone = pickField(o, "phoneNumber", "phone_number", "contactNo");
                        const email = pickField(o, "email");
                        const openRow = isPending
                          ? id ? () => continuePendingOrganizationCreate(id) : null
                          : detailPath ? () => navigate(detailPath) : null;
                        return (
                        <tr
                          key={`${id || i}`}
                          className={openRow ? "eats-admin-row--clickable" : undefined}
                          onClick={rowClickHandler(openRow)}
                        >
                          <td>
                            <AdminNameCell
                              name={name}
                              id={id}
                              code={o.organizationCode}
                              status={status}
                              to={detailPath && !isPending ? detailPath : null}
                            />
                          </td>
                          <td>{pickField(o, "organizationType", "organization_type") || "-"}</td>
                          <td>
                            <LocationCell location={getRestaurantLocation(o)} mapUrl={getGoogleMapsUrl(o, name)} />
                          </td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            {formatMobileDisplayOrFallback(phone, "-")}
                          </td>
                          <td className="eats-admin-cell-email">{email || "-"}</td>
                          <td className="eats-admin-col-code" style={{ whiteSpace: "nowrap" }}>
                            {o.organizationCode ? String(o.organizationCode).toUpperCase() : "-"}
                          </td>
                          <td className="eats-admin-col-status">{renderStatusBadge(status)}</td>
                          <td className="eats-admin-col-actions">
                            <AdminRowActions
                              name={name}
                              phone={phone}
                              email={email}
                              mapUrl={getRestaurantLocation(o) !== "-" ? getGoogleMapsUrl(o, name) : null}
                              onContinue={isPending && id ? () => continuePendingOrganizationCreate(id) : null}
                              onView={!isPending && detailPath ? () => navigate(detailPath) : null}
                              onEdit={!isPending && detailPath ? () => navigate(`${detailPath}?edit=1`) : null}
                              onDeactivate={
                                !isPending && detailPath && isActive ? () => deactivateOrganization(id, name) : null
                              }
                            />
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <AdminTablePagination
                  page={orgPage}
                  totalItems={orgResults.length}
                  onPageChange={setOrgPage}
                />
              </div>
            ) : null}

            {activeTab === "restaurants" ? (
              <div className="eats-admin-panel">
                <div className="eats-admin-search-bar eats-admin-search-bar--restaurants">
                  <AdminClearableInput
                    placeholder="Search by restaurant text"
                    value={restaurantSearch}
                    onChange={(e) => setRestaurantSearch(e.target.value)}
                    onClear={() => {
                      setRestaurantSearch("");
                      setRestaurantError("");
                      setRestaurantResults(restaurantStatusFilter === "PENDING" ? pendingCompanies : []);
                    }}
                    suggestions={restaurantSuggestions}
                    getSuggestionKey={(r, i) => extractRestaurantId(r) || r.restaurantId || i}
                    renderSuggestion={(r) => {
                      const city =
                        restaurantStatusFilter === "PENDING"
                          ? String(r.restaurantShortCode || "").toUpperCase()
                          : getRestaurantDisplayCity(r);
                      return (
                        <>
                          <span className="eats-admin-suggestions__name">
                            {extractRestaurantName(r) || r.restaurantName || "-"}
                          </span>
                          {city && city !== "-" ? <span className="eats-admin-suggestions__meta">{city}</span> : null}
                        </>
                      );
                    }}
                    onSelectSuggestion={selectRestaurantSuggestion}
                    onEnter={searchRestaurants}
                  />
                  <CollapsibleFilters activeCount={restaurantStatusFilter !== "ACTIVE" ? 1 : 0}>
                    <select
                      className="eats-admin-input eats-admin-status-filter"
                      value={restaurantStatusFilter}
                      onChange={(e) => handleRestaurantStatusFilterChange(e.target.value)}
                      aria-label="Restaurant status"
                    >
                      <option value="ACTIVE">Active</option>
                      <option value="INACTIVE">Inactive</option>
                      <option value="PENDING">Pending</option>
                      <option value="ALL">All</option>
                    </select>
                  </CollapsibleFilters>
                  <button
                    type="button"
                    onClick={searchRestaurants}
                    disabled={restaurantLoading}
                    className="eats-admin-btn eats-admin-btn--primary"
                  >
                    {restaurantLoading ? "Searching..." : "Search"}
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate("/eats/admin/restaurants/create")}
                    className="eats-admin-btn eats-admin-btn--create"
                  >
                    Create Restaurant
                  </button>
                </div>

                {restaurantError ? <div className="eats-admin-error">{restaurantError}</div> : null}
                <div className="eats-admin-table-wrap">
                  <table className="eats-admin-table eats-admin-table--restaurants eats-admin-table--entities">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Contact Name</th>
                        <th>Location</th>
                        <th>Phone</th>
                        <th>FSSAI Number</th>
                        <th>Email</th>
                        <th className="eats-admin-col-status">Status</th>
                        <th className="eats-admin-col-actions">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {restaurantResults.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="eats-admin-table__empty">
                            {restaurantStatusFilter === "PENDING"
                              ? "No pending restaurants."
                              : "No restaurant results."}
                          </td>
                        </tr>
                      ) : restaurantResults
                        .slice((restaurantPage - 1) * ADMIN_ROWS_PER_PAGE, restaurantPage * ADMIN_ROWS_PER_PAGE)
                        .map((r, i) => {
                        const id = extractRestaurantId(r) || r.restaurantId;
                        const name = extractRestaurantName(r) || r.restaurantName || "-";
                        const isPending = restaurantStatusFilter === "PENDING";
                        const isActive = String(r.status || "").toUpperCase() === "ACTIVE";
                        const detailPath = id ? `/eats/admin/restaurants/${id}` : null;
                        const phone = pickField(r, "contact_no", "contactNo", "contactNumber");
                        const email = pickField(r, "email");
                        const openRow = isPending
                          ? id ? () => continuePendingRestaurantCreate(id) : null
                          : detailPath ? () => navigate(detailPath) : null;
                        return (
                        <tr
                          key={`${id || i}`}
                          className={openRow ? "eats-admin-row--clickable" : undefined}
                          onClick={rowClickHandler(openRow)}
                        >
                          <td>
                            <AdminNameCell
                              name={name}
                              id={id}
                              code={pickField(r, "restaurantShortCode", "restaurant_short_code", "shortCode")}
                              status={r.status}
                              to={detailPath && !isPending ? detailPath : null}
                            />
                          </td>
                          <td>{pickField(r, "contact_name", "contactName", "ownerName") || "-"}</td>
                          <td>
                            <LocationCell location={getRestaurantLocation(r)} mapUrl={getGoogleMapsUrl(r, name)} />
                          </td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            {formatMobileDisplayOrFallback(phone, "-")}
                          </td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            {formatFssaiDisplayOrFallback(pickField(r, "fssai_number", "fssaiNumber"), "-")}
                          </td>
                          <td className="eats-admin-cell-email">{email || "-"}</td>
                          <td className="eats-admin-col-status">{renderStatusBadge(r.status)}</td>
                          <td className="eats-admin-col-actions">
                            <AdminRowActions
                              name={name}
                              phone={phone}
                              email={email}
                              mapUrl={getRestaurantLocation(r) !== "-" ? getGoogleMapsUrl(r, name) : null}
                              onContinue={isPending ? () => continuePendingRestaurantCreate(id) : null}
                              onView={!isPending && detailPath ? () => navigate(detailPath) : null}
                              onEdit={!isPending && detailPath ? () => navigate(`${detailPath}?edit=1`) : null}
                              onDeactivate={!isPending && detailPath && isActive ? () => deactivateRestaurant(id, name) : null}
                            />
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <AdminTablePagination
                  page={restaurantPage}
                  totalItems={restaurantResults.length}
                  onPageChange={setRestaurantPage}
                />
              </div>
            ) : null}

            {activeTab === "stores" ? (
              <div className="eats-admin-panel">
                <div className="eats-admin-search-bar eats-admin-search-bar--stores">
                  <AdminClearableInput
                    placeholder="Search by store name, code, or ID"
                    value={storeSearch}
                    onChange={(e) => setStoreSearch(e.target.value)}
                    onClear={() => {
                      setStoreSearch("");
                      setStoreError("");
                      setStoreResults(storeStatusFilter === "PENDING" ? pendingStores : []);
                    }}
                    suggestions={storeSuggestions}
                    getSuggestionKey={(s, i) => extractStoreId(s) || i}
                    renderSuggestion={(s) => {
                      const meta =
                        storeStatusFilter === "PENDING" ? String(s.shortCode || "").toUpperCase() : getStoreDisplayCity(s);
                      return (
                        <>
                          <span className="eats-admin-suggestions__name">{extractStoreName(s) || "-"}</span>
                          {meta && meta !== "-" ? <span className="eats-admin-suggestions__meta">{meta}</span> : null}
                        </>
                      );
                    }}
                    onSelectSuggestion={selectStoreSuggestion}
                    onEnter={searchStores}
                  />
                  <CollapsibleFilters activeCount={storeStatusFilter !== "ACTIVE" ? 1 : 0}>
                    <select
                      className="eats-admin-input eats-admin-status-filter"
                      value={storeStatusFilter}
                      onChange={(e) => handleStoreStatusFilterChange(e.target.value)}
                      aria-label="Store status"
                    >
                      <option value="ACTIVE">Active</option>
                      <option value="INACTIVE">Inactive</option>
                      <option value="PENDING">Pending</option>
                      <option value="ALL">All</option>
                    </select>
                  </CollapsibleFilters>
                  <button
                    type="button"
                    onClick={searchStores}
                    disabled={storeLoading}
                    className="eats-admin-btn eats-admin-btn--primary"
                  >
                    {storeLoading ? "Searching..." : "Search"}
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate("/eats/admin/stores/create")}
                    className="eats-admin-btn eats-admin-btn--create"
                  >
                    Create Store
                  </button>
                </div>

                {storeError ? <div className="eats-admin-error">{storeError}</div> : null}
                <div className="eats-admin-table-wrap">
                  <table className="eats-admin-table eats-admin-table--restaurants eats-admin-table--entities">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Contact Name</th>
                        <th>Location</th>
                        <th>Phone</th>
                        <th>Licence</th>
                        <th>Email</th>
                        <th className="eats-admin-col-code">Short Code</th>
                        <th className="eats-admin-col-status">Status</th>
                        <th className="eats-admin-col-actions">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {storeResults.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="eats-admin-table__empty">
                            {storeStatusFilter === "PENDING" ? "No pending stores." : "No store results."}
                          </td>
                        </tr>
                      ) : storeResults
                        .slice((storePage - 1) * ADMIN_ROWS_PER_PAGE, storePage * ADMIN_ROWS_PER_PAGE)
                        .map((s, i) => {
                        const id = extractStoreId(s);
                        const name = extractStoreName(s) || "-";
                        const isPending = storeStatusFilter === "PENDING";
                        const isActive = String(s.status || "").toUpperCase() === "ACTIVE";
                        const detailPath = id ? `/eats/admin/stores/${id}` : null;
                        const phone = pickField(s, "contactNo", "contact_no", "contactNumber");
                        const email = pickField(s, "email");
                        const openRow = isPending
                          ? id ? () => continuePendingStoreCreate(id) : null
                          : detailPath ? () => navigate(detailPath) : null;
                        return (
                        <tr
                          key={`${id || i}`}
                          className={openRow ? "eats-admin-row--clickable" : undefined}
                          onClick={rowClickHandler(openRow)}
                        >
                          <td>
                            <AdminNameCell
                              name={name}
                              id={id}
                              code={s.shortCode}
                              status={s.status}
                              to={detailPath && !isPending ? detailPath : null}
                            />
                          </td>
                          <td>{pickField(s, "contactName", "contact_name", "ownerName") || "-"}</td>
                          <td>
                            <LocationCell location={getRestaurantLocation(s)} mapUrl={getGoogleMapsUrl(s, name)} />
                          </td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            {formatMobileDisplayOrFallback(phone, "-")}
                          </td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            {(() => {
                              const licence = String(
                                pickField(s, "licenseNumber", "licenceNumber", "fssaiNumber", "fssai_number") || ""
                              ).trim();
                              if (!licence) return "-";
                              return /^\d+$/.test(licence) ? formatFssaiDisplayOrFallback(licence, "-") : licence;
                            })()}
                          </td>
                          <td className="eats-admin-cell-email">{email || "-"}</td>
                          <td className="eats-admin-col-code" style={{ whiteSpace: "nowrap" }}>{s.shortCode ? String(s.shortCode).toUpperCase() : "-"}</td>
                          <td className="eats-admin-col-status">{renderStatusBadge(s.status)}</td>
                          <td className="eats-admin-col-actions">
                            <AdminRowActions
                              name={name}
                              phone={phone}
                              email={email}
                              mapUrl={getRestaurantLocation(s) !== "-" ? getGoogleMapsUrl(s, name) : null}
                              onContinue={isPending && id ? () => continuePendingStoreCreate(id) : null}
                              onView={!isPending && detailPath ? () => navigate(detailPath) : null}
                              onEdit={!isPending && detailPath ? () => navigate(`${detailPath}?edit=1`) : null}
                              onDeactivate={!isPending && detailPath && isActive ? () => deactivateStore(id, name) : null}
                            />
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <AdminTablePagination
                  page={storePage}
                  totalItems={storeResults.length}
                  onPageChange={setStorePage}
                />
              </div>
            ) : null}

            {activeTab === "customers" ? (
              <div className="eats-admin-panel">
                <div className="eats-admin-search-bar eats-admin-search-bar--customers">
                  <AdminClearableInput
                    placeholder="Name"
                    value={customerCriteria.name}
                    onChange={(e) => setCustomerCriteria((p) => ({ ...p, name: e.target.value }))}
                    onClear={() => clearCustomerFilterField("name")}
                  />
                  <CollapsibleFilters
                    activeCount={["contactNo", "createdSource"].filter((k) =>
                      String(customerCriteria[k] || "").trim()
                    ).length}
                  >
                    {[
                      ["contactNo", "Contact Number"],
                      ["createdSource", "Created Source"],
                    ].map(([k, label]) => (
                      <AdminClearableInput
                        key={k}
                        placeholder={label}
                        value={customerCriteria[k]}
                        onChange={(e) => setCustomerCriteria((p) => ({ ...p, [k]: e.target.value }))}
                        onClear={() => clearCustomerFilterField(k)}
                      />
                    ))}
                  </CollapsibleFilters>
                  <button
                    type="button"
                    onClick={searchCustomers}
                    disabled={customerLoading}
                    className="eats-admin-btn eats-admin-btn--primary"
                  >
                    {customerLoading ? "Searching..." : "Search"}
                  </button>
                </div>
                {customerError ? <div className="eats-admin-error">{customerError}</div> : null}
                <div className="eats-admin-table-wrap eats-admin-table-wrap--tall">
                  <table className="eats-admin-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Contact</th>
                        <th>Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customerResults.length === 0 ? (
                        <tr><td colSpan={5} className="eats-admin-table__empty">No customer results.</td></tr>
                      ) : customerResults
                        .slice((customerPage - 1) * ADMIN_ROWS_PER_PAGE, customerPage * ADMIN_ROWS_PER_PAGE)
                        .map((c) => (
                        <tr key={c.customerId}>
                          <td>{c.customerId}</td>
                          <td>{`${c.firstName || ""} ${c.lastName || ""}`.trim() || "-"}</td>
                          <td>{c.email || "-"}</td>
                          <td style={{ whiteSpace: "nowrap", minWidth: 130 }}>{formatMobileDisplayOrFallback(c.contactNumber, "-")}</td>
                          <td>{c.createdSource || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <AdminTablePagination
                  page={customerPage}
                  totalItems={customerResults.length}
                  onPageChange={setCustomerPage}
                />
              </div>
            ) : null}

            {activeTab === "subscriptions" ? (
              <div className="eats-admin-panel">
                <JuniorEatsSubscriptionsPage />
              </div>
            ) : null}

            {activeTab === "jreats-menu" ? (
              <div className="eats-admin-panel">
                <JuniorEatsMainMenuPage />
              </div>
            ) : null}

            {activeTab === "menu-master" ? (
              <div className="eats-admin-panel">
                <GlobalMenuMasterPage />
              </div>
            ) : null}

            {activeTab === "pickup-hubs" ? (
              <div className="eats-admin-panel">
                <PickupHubsPage />
              </div>
            ) : null}
          </div>
    </EatsAdminLayout>
  );
};

export default EatsAdminDashboard;
